import { AxiosError } from 'axios';

const DEFAULT_ERROR = 'Something went wrong';

export enum ErrorMessageMode {
  KEY_VALUES = 'key_values',
  VALUES = 'values',
}

export interface IErrorResponse {
  error?: string | string[];
  errors?: Record<string, string | string[]>;
}

function getNormalizedError(errors: Record<string, string | string[]>) {
  const normalizedErrors: Record<string, string> = {};
  Object.entries(errors).forEach(([k, v]) => {
    if (v !== null) {
      const value = typeof v === 'string' ? v : v.join(', ');
      normalizedErrors[k] = value;
    }
  });
  return normalizedErrors;
}

export function getAxiosErrorMessage(
  err: AxiosError<IErrorResponse>,
): string | Record<string, string> | string[] {
  const data = err.response?.data;
  if (!data) {
    return DEFAULT_ERROR;
  }

  // api returns errors with the keys as error or errors
  // error value could be a string, array of string or an object
  // we consider all the cases
  let errors;
  if (data.error) {
    errors = data.error;
  } else if (data.errors) {
    errors = data.errors;
  } else {
    errors = DEFAULT_ERROR;
  }
  if (typeof errors === 'string') {
    return errors;
  }

  if (Array.isArray(errors)) {
    return errors;
  }

  if (typeof errors === 'object') {
    const normalizedErrors = getNormalizedError(errors);
    return normalizedErrors;
  }

  return DEFAULT_ERROR;
}

export function convertErrorObjToString(
  errors: Record<string, string>,
  mode: ErrorMessageMode,
): string {
  // we get error response from api like
  // {
  //   errors: {
  //     Auth: 'incorrect username or password'
  //   }
  // }
  // so we use only the value
  // for example - incorrect username or password
  if (mode === ErrorMessageMode.VALUES) {
    return Object.values(errors).join(', ');
  }

  // we get error response from api like
  // {
  //   errors: {
  //     Intent: 'or account id already exists'
  //   }
  // }
  // so we concatenate the key and value to get complete string
  // for example - Intent or account id already exists
  return Object.entries(errors)
    .map(([k, v]) => k + ' ' + v)
    .join(', ');
}

export function getAxiosErrorMessageAsString(
  err: AxiosError<IErrorResponse>,
  mode: ErrorMessageMode = ErrorMessageMode.VALUES,
): string | string[] {
  const error = getAxiosErrorMessage(err);
  if (typeof error === 'string') {
    return error;
  }
  if (Array.isArray(error)) {
    return error.join(', ');
  }
  return convertErrorObjToString(error, mode);
}
